import { useCallback, useMemo } from 'react';
import { match, P } from 'ts-pattern';
import { ContactModalTabKey } from 'src/components/domain/contacts/contact-modal/contact-modal.shared';
import { InternalNoteType } from 'src/components/pages/conversations/components/contact-interactions/items/shared';
import { useInstitutionUrl } from 'src/hooks/use-institution-url';
import { ConversationStatus } from 'src/services/conversation.service';
import { useAuthorization } from './use-authorization';

type ID = string | number;
type UseUrlOptions = { includeHost: boolean };

export const useUrls = (options?: { scope: 'COMPANY' }) => {
  const { getFeatureFlag } = useAuthorization();
  const institutionUrl = useInstitutionUrl();
  const hostResolver = useCallback((url: string, options?: UseUrlOptions) => {
    if (options?.includeHost) {
      return `${window.location.origin}${url}`;
    } else {
      return url;
    }
  }, []);

  const getCompanyUrl = useCallback(
    (companyId?: number) => (companyId ? `/i/company/${companyId}/` : institutionUrl),
    [institutionUrl],
  );

  const checkScope = useCallback(
    (companyId?: number) => {
      if (options?.scope === 'COMPANY' && !companyId) {
        throw new Error('Company id is required for company scope');
      }
    },
    [options?.scope],
  );

  const getLegacyAddContactInteractionUrl = useCallback(
    (contactId: ID) =>
      `${institutionUrl}cms/_modals/client/items/activity/edit/lead_id:${contactId}/`,
    [institutionUrl],
  );

  const getEditContactInteractionUrl = useCallback(
    (contactInteractionId: ID) =>
      `${institutionUrl}comunication-interaction/${contactInteractionId}`,
    [institutionUrl],
  );

  const showInlineForm =
    getFeatureFlag('inline-forms-redesign') && getFeatureFlag('ux-redesign-v2.5');

  const getLegacyContactEditUrl = useCallback(
    (contactId?: ID) =>
      `${institutionUrl}cms/_modals/client/edit/${
        contactId ? `lead_id:${contactId}` : ''
      }`,
    [institutionUrl],
  );

  const getLegacyAddTaskUrl = useCallback(
    ({ contactId, targetDate }: { contactId?: ID; targetDate?: string }) => {
      let targetUrl = `${institutionUrl}cms/_modals/task/edit/`;

      if (contactId) {
        targetUrl += `lead_id:${contactId}`;
      }

      if (targetDate) {
        targetUrl += `?date=${targetDate}`;
      }

      return targetUrl;
    },
    [institutionUrl],
  );

  const getLegacyTaskEditUrl = useCallback(
    (taskId?: ID) => `${institutionUrl}cms/_modals/task/edit/id:${taskId}/`,
    [institutionUrl],
  );

  const getLegacyContactAddNoteUrl = useCallback(
    (contactId: ID) =>
      `${institutionUrl}cms/_modals/client/items/note/edit/lead_id:${contactId}/`,
    [institutionUrl],
  );

  const getLegacyContactEditNoteUrl = useCallback(
    (contactId: ID, noteId: ID) =>
      `${institutionUrl}cms/_modals/client/items/note/edit/id:${noteId}/lead_id:${contactId}`,
    [institutionUrl],
  );

  const getLegacyAddScheduleUrl = useCallback(
    ({ contactId, targetDate }: { contactId?: ID; targetDate?: string }) => {
      let targetUrl = `${institutionUrl}cms/_modals/schedule/edit/`;

      if (contactId) {
        targetUrl += `lead_id:${contactId}`;
      }

      if (targetDate) {
        targetUrl += `?date=${targetDate}`;
      }

      return targetUrl;
    },
    [institutionUrl],
  );

  const getLegacyScheduleEditUrl = useCallback(
    (scheduleId: ID, contactId?: ID) => {
      let targetUrl = `${institutionUrl}cms/_modals/schedule/edit/id:${scheduleId}/`;

      if (contactId) {
        targetUrl += `lead_id:${contactId}`;
      }

      return targetUrl;
    },
    [institutionUrl],
  );

  return useMemo(
    () => ({
      // Contact routes
      getLegacyContactEditUrl,
      getEditContactUrl: (contactId: ID) => {
        if (showInlineForm) {
          return `${institutionUrl}contact/${contactId}`;
        }

        return getLegacyContactEditUrl(contactId);
      },
      getAddContactUrl: () => {
        if (showInlineForm) {
          return `${institutionUrl}contact/create`;
        }

        return getLegacyContactEditUrl();
      },
      // Tasks
      getLegacyAddTaskUrl,
      getLegacyTaskEditUrl,
      getTaskEditUrl: ({ taskId, options }: { taskId?: ID; options?: UseUrlOptions }) => {
        if (showInlineForm) {
          const url = `${institutionUrl}task/${taskId}`;

          return hostResolver(url, options);
        }

        return hostResolver(getLegacyTaskEditUrl(taskId), options);
      },
      getAddTaskUrl: ({
        contactId,
        targetDate,
        options,
      }: {
        contactId?: ID;
        targetDate?: string;
        options?: UseUrlOptions;
      } = {}) => {
        if (showInlineForm) {
          const params: string[] = [];

          if (contactId) {
            params.push(`contactId=${contactId}`);
          }

          if (targetDate) {
            params.push(`date=${targetDate}`);
          }

          const url = `${institutionUrl}task/create${
            params.length > 0 ? `?${params.join('&')}` : ''
          }`;

          return hostResolver(url, options);
        }

        return hostResolver(getLegacyAddTaskUrl({ contactId, targetDate }), options);
      },
      // contact note
      getLegacyContactAddNoteUrl,
      getLegacyContactEditNoteUrl,
      getAddContactNoteUrl: (contactId: ID) => {
        if (showInlineForm) {
          return `${institutionUrl}contact-note/create?contactId=${contactId}`;
        }

        return getLegacyContactAddNoteUrl(contactId);
      },
      getEditContactNoteUrl: ({
        contactNoteId,
        contactId,
        options,
      }: {
        contactNoteId: ID;
        contactId: ID;
        options?: UseUrlOptions;
      }) => {
        if (showInlineForm) {
          const url = `${institutionUrl}contact-note/${contactNoteId}`;

          return hostResolver(url, options);
        }

        return hostResolver(
          getLegacyContactEditNoteUrl(contactId, contactNoteId),
          options,
        );
      },
      // Appointments
      getLegacyAddScheduleUrl,
      getLegacyScheduleEditUrl,
      getAddScheduleUrl: ({
        contactId,
        targetDate,
        options,
      }: {
        contactId?: ID;
        targetDate?: string;
        options?: UseUrlOptions;
      } = {}) => {
        if (showInlineForm) {
          const params: string[] = [];

          if (contactId) {
            params.push(`contactId=${contactId}`);
          }

          if (targetDate) {
            params.push(`date=${targetDate}`);
          }

          return hostResolver(
            `${institutionUrl}appointment/create${
              params.length > 0 ? `?${params.join('&')}` : ''
            }`,
            options,
          );
        }

        return hostResolver(getLegacyAddScheduleUrl({ contactId, targetDate }), options);
      },
      getAppointmentUrl: ({
        appointmentId,
        targetDate,
        options,
      }: {
        appointmentId: ID;
        targetDate?: string;
        options?: UseUrlOptions;
      }) => {
        if (showInlineForm) {
          const url = `${institutionUrl}appointment/${appointmentId}${
            targetDate ? `?date=${targetDate}` : ''
          }`;

          return hostResolver(url, options);
        }

        return getLegacyScheduleEditUrl(appointmentId, targetDate);
      },
      // Contact interactions
      getLegacyAddContactInteractionUrl,
      getAddContactInteractionUrl: (contactId: ID) => {
        if (showInlineForm) {
          return `${institutionUrl}comunication-interaction/create?contactId=${contactId}`;
        }

        return getLegacyAddContactInteractionUrl(contactId);
      },
      getEditContactInteractionUrl,
      getFacebookSettingsPageUrl: (params?: { companyId?: number }) => {
        const companyId = params?.companyId;

        checkScope(companyId);

        return `${getCompanyUrl(companyId)}settings/facebook/`;
      },
      getLegacyContactCallUrl: (contactId: ID) =>
        `${institutionUrl}_modals/call?id=${contactId}/`,
      getContactCallUrl: (contactId: ID) =>
        `${institutionUrl}modal/call/contact/${contactId}/`,
      getLegacyContactAddInteractionUrl: (contactId: ID) =>
        `${institutionUrl}cms/_modals/client/items/activity/edit/lead_id:${contactId}/`,
      getContactModalUrl: ({
        contactId,
        tab,
        companyId,
      }: {
        contactId: number;
        companyId?: number;
        tab?:
          | ContactModalTabKey
          | {
              conversationId?: string;
              contactInteractionId?: string;
            };
      }) => {
        checkScope(companyId);

        const baseUrl = `${getCompanyUrl(companyId)}modal/contact/${contactId}`;
        return match(tab)
          .with('tasks', () => `${baseUrl}/tasks`)
          .with('appointments', () => `${baseUrl}/appointments`)
          .with('notes', () => `${baseUrl}/notes`)
          .with('contact-details', () => `${baseUrl}/contact-details`)
          .with(
            P.union(
              'conversations',
              {
                conversationId: P.string,
              },
              {
                conversationId: P.string,
                contactInteractionId: P.number,
              },
            ),
            (value) => {
              const baseConversationUrl = `${baseUrl}/conversations`;
              if (value === 'conversations') {
                return baseConversationUrl;
              }

              const { conversationId, contactInteractionId } = value;

              if (value.contactInteractionId) {
                return `${baseUrl}/conversations/${conversationId}/${contactInteractionId}`;
              }

              return `${baseUrl}/conversations/${conversationId}`;
            },
          )
          .otherwise(() => baseUrl);
      },
      getTaskModalUrl: ({ taskId, companyId }: { taskId: ID; companyId?: number }) => {
        checkScope(companyId);

        return `${getCompanyUrl(companyId)}modal/task/${taskId}`;
      },
      getTaskInlineFormUrl: (taskId: ID) => `${institutionUrl}task/${taskId}`,
      getTaskModalEditForContactUrl: (contactId: ID) =>
        `${institutionUrl}modal/task/edit/contact/${contactId}`,

      getScheduleModalUrl: ({
        scheduleId,
        contactId,
        companyId,
      }: {
        scheduleId: ID;
        contactId?: ID;
        companyId?: number;
      }) => {
        checkScope(companyId);

        const url = `${getCompanyUrl(companyId)}modal/schedule/edit/${scheduleId}`;

        if (contactId) {
          return `${url}/contact/${contactId}`;
        }

        return url;
      },
      getScheduleModalEditForContactUrl: (contactId?: ID) =>
        `${institutionUrl}modal/schedule/edit/contact/${contactId}`,
      getLegacyContactFollowUpUrl: (contactId: ID) =>
        `${institutionUrl}cms/_modals/task/followup/lead_id:${contactId}/`,
      getLegacyContactDetailUrl: (contactId: ID) =>
        `${institutionUrl}cms/_modals/client/lead_id:${contactId}/`,
      getLegacyContactImportUrl: () => `${institutionUrl}contacts/import/`,
      getLegacyContactStatusModalUrl: (contactId: ID) =>
        `${institutionUrl}cms/_modals/client/status/lead_id:${contactId}/`,
      getLegacyContactInterestModalUrl: (contactId: ID) =>
        `${institutionUrl}cms/_modals/client/interest/lead_id:${contactId}/`,
      getLegacyContactAssigneeModalUrl: (contactId: ID) =>
        `${institutionUrl}cms/_modals/client/owner/lead_id:${contactId}/`,
      getLegacyDebugCallUrl: (activityId: ID) =>
        `${institutionUrl}activity/_system/debug_call/a_id:${activityId}/`,
      getLegacyDebugNotificationUrl: (activityId: ID) =>
        `${institutionUrl}activity/_system/check_notifications/a_id:${activityId}/`,
      getLegacyContactConversationModalUrl: (contactId: ID) =>
        `${institutionUrl}_modals/lead/conversation/id:${contactId}`,
      getLegacyContactInteractionModalUrl: (contactInteractionId: string) =>
        `${institutionUrl}_modals/lead/history/id:${contactInteractionId}/`,
      getLegacyContactInteractionDepartmentModalUrl: (activityId: string) =>
        `${institutionUrl}_modals/lead/history/department/a_id:${activityId}/`,
      getConversationUrl: (conversationId: string) =>
        `${institutionUrl}conversations/${conversationId}`,
      getConversationsUrl: (conversationStatus?: ConversationStatus) =>
        `${institutionUrl}conversations/${
          conversationStatus ? `?conversation_status=${conversationStatus}` : ''
        }`,
      getConversationContactInteractionUrl: (params: {
        conversationId: string;
        contactInteractionId: ID;
        options?: UseUrlOptions;
      }) => {
        const { conversationId, contactInteractionId, options } = params;
        const url = `${institutionUrl}conversations/${conversationId}/${contactInteractionId}`;

        return hostResolver(url, options);
      },
      getContactInteractionUrl: ({
        contactInteractionId,
        companyId,
        options,
      }: {
        contactInteractionId: ID;
        options?: UseUrlOptions;
        companyId?: number;
      }) => {
        checkScope(companyId);

        return hostResolver(
          `${getCompanyUrl(companyId)}contact/interaction/${contactInteractionId}/`,
          options,
        );
      },
      getInternalNoteUrl: (params: {
        conversationId: string;
        noteId: string;
        companyId?: number;
        options?: UseUrlOptions;
        isReplying?: boolean;
        noteType: InternalNoteType;
      }) => {
        const {
          conversationId,
          noteId,
          options,
          isReplying = false,
          noteType,
          companyId,
        } = params;
        checkScope(companyId);

        const isConversationNote = noteType === 'CONVERSATION_NOTE';
        const urlNoteId = `${isConversationNote ? 'note' : 'ctnote'}-${noteId}`;

        return hostResolver(
          `${getCompanyUrl(companyId)}conversations/${conversationId}/${urlNoteId}${
            isConversationNote && isReplying ? '?reply=true' : ''
          }`,
          options,
        );
      },
      getLegacyEditUserUrl: () => `${institutionUrl}_actions/account/view/edit`,
      getWhatsappResourceUrl: (contactId: ID) =>
        `${institutionUrl}whatsapp-channel/${contactId}`,
      getBroadcastPageUrl: () => `${institutionUrl}broadcast`,
      getEditBroadcastUrl: (id: ID) => `${institutionUrl}broadcast/${id}`,
      getAddNewBroadcastUrl: () => `${institutionUrl}broadcast/add-new`,
      getAddNewContactUrl: () => `${institutionUrl}contact/create`,
    }),
    [
      checkScope,
      getCompanyUrl,
      getEditContactInteractionUrl,
      getLegacyAddContactInteractionUrl,
      getLegacyAddScheduleUrl,
      getLegacyAddTaskUrl,
      getLegacyContactAddNoteUrl,
      getLegacyContactEditNoteUrl,
      getLegacyContactEditUrl,
      getLegacyScheduleEditUrl,
      getLegacyTaskEditUrl,
      hostResolver,
      institutionUrl,
      showInlineForm,
    ],
  );
};
